import {
  BookOpen,
  Building2,
  ChevronRight,
  Globe,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Moon,
  Search,
  Settings,
  Sun,
  Upload,
  User as UserIcon,
  X,
} from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { HelpMenu } from '@/components/HelpMenu';
import { LogoLockup } from '@/components/Logo';
import { NotificationsBell } from '@/components/NotificationsBell';
import { useActiveOrg } from '@/components/OrgPicker';
import { PaneFallback, Sheet } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/classnames';
import { useMediaQuery } from '@/lib/useMediaQuery';

export default function AppLayout() {
  const { theme, toggleTheme } = useTheme();
  const loc = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  // The top-nav box is a launcher for the Submissions search, which owns the
  // actual matching (filename / task ID / SHA-256, sample or dropped file).
  const runGlobalSearch = () => {
    const q = globalSearch.trim();
    if (!q) return;
    navigate('/submissions', { state: { q } });
    setGlobalSearch('');
  };

  const isAdminArea = loc.pathname.startsWith('/admin');

  // Breadcrumb logic. At a section's root the trailing crumb IS the page, so
  // it stays a span - a link to the page you are on is focusable noise. On
  // deeper paths (/submissions/:id) it links back to the section.
  const pathParts = loc.pathname.split('/').filter(Boolean);
  const currentSection = pathParts[0] || 'dashboard';
  const atSectionRoot = pathParts.length <= 1;

  // Close mobile menu on navigate
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [loc.pathname]);

  // The window never scrolls inside the shell - .page-content does - so a
  // navigation must reset it by hand or the next page opens mid-scroll.
  // Keyed on the path alone: search-param writes (the report's ?tab=) are
  // same-page state and own their scroll behavior.
  useEffect(() => {
    document.getElementById('main-content')?.scrollTo({ top: 0 });
  }, [loc.pathname]);

  // Widening past md swaps the drawer for the persistent rail. Close it for
  // real rather than hiding it in CSS - a hidden vaul panel still leaves its
  // scrim and scroll lock behind, which reads as a frozen page.
  const isDesktop = useMediaQuery('(min-width: 768px)');
  useEffect(() => {
    if (isDesktop) setMobileMenuOpen(false);
  }, [isDesktop]);

  return (
    <div className="layout">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-ink-900 focus:text-ink-50 focus:ring-2 focus:ring-accent-400"
      >
        Skip to content
      </a>

      {/* Persistent rail - md and up. Below md the same nav lives in the
          drawer below, so there is one nav tree, not two that can drift. */}
      <aside className="sidebar hidden md:flex">
        <SidebarBody />
      </aside>

      {/* Phone / small-tablet drawer. Sheet is vaul over Radix Dialog, so this
          gets the focus trap, Escape, scrim, body scroll lock and swipe-to-
          close that the old hand-rolled `absolute` panel had none of. */}
      <Sheet
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        direction="left"
        showHandle={false}
        contentClassName="bg-[var(--bg-sidebar)]"
      >
        <SidebarBody onClose={() => setMobileMenuOpen(false)} />
      </Sheet>

      {/* Main Workspace */}
      <main className="workspace">
        {/* Topbar */}
        <header className="topbar">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button
              type="button"
              className="md:hidden btn-icon"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation"
              aria-expanded={mobileMenuOpen}
              aria-haspopup="dialog"
            >
              <Menu size={20} />
            </button>
            {/* The full trail needs room, the current section does not. Phones
                keep the section name rather than losing their bearings. */}
            <nav aria-label="Breadcrumb" className="breadcrumbs">
              <Link
                to={isAdminArea ? '/admin' : '/dashboard'}
                className="breadcrumb-item capitalize hidden sm:inline"
              >
                {isAdminArea ? 'Administration' : 'Platform'}
              </Link>
              <ChevronRight className="breadcrumb-sep hidden sm:block" />
              {atSectionRoot ? (
                <span aria-current="page" className="breadcrumb-item current capitalize truncate">
                  {currentSection.replace('-', ' ')}
                </span>
              ) : (
                <Link
                  to={`/${currentSection}`}
                  className="breadcrumb-item current capitalize truncate"
                >
                  {currentSection.replace('-', ' ')}
                </Link>
              )}
            </nav>
          </div>

          <div className="topbar-actions">
            <div className="search-box hidden lg:flex">
              <Search />
              <input
                type="text"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runGlobalSearch();
                }}
                placeholder="Filename or hash"
                aria-label="Search submissions"
              />
            </div>

            <button
              type="button"
              onClick={() => navigate('/docs')}
              className="btn-icon"
              aria-label="API documentation"
              title="API documentation"
            >
              <BookOpen size={18} />
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              className="btn-icon"
              aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <HelpMenu />
            <NotificationsBell />

            <button
              type="button"
              onClick={() => navigate('/submit')}
              className="btn-primary hidden sm:inline-flex whitespace-nowrap px-5"
            >
              <Upload size={16} className="shrink-0" />
              New Analysis
            </button>
            {/* Same action, icon-only, so the primary CTA survives a phone
                instead of retreating into the drawer. It is .btn-primary and
                not a hand-rolled square because the coarse-pointer target
                floor is attached to the button classes: styled by hand, the
                most-tapped control on the page sat at roughly 34px, under the
                44px the rest of the product now guarantees. */}
            <button
              type="button"
              onClick={() => navigate('/submit')}
              className="btn-primary sm:hidden"
              aria-label="New analysis"
            >
              <Upload size={18} />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content" id="main-content">
          <Suspense fallback={<PaneFallback />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}

// The nav tree itself, rendered by both the md+ rail and the drawer from one
// definition. `onClose` is supplied only by the drawer, which is also the only
// context that needs a visible dismiss control.
function SidebarBody({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAuth();
  const { orgId } = useActiveOrg();
  const loc = useLocation();

  const isAdminArea = loc.pathname.startsWith('/admin');
  const isOrgSettingsArea = loc.pathname.startsWith('/org/');
  const managedOrg =
    user?.memberships?.find((m) => m.role === 'manager' || m.role === 'org_admin') ??
    user?.memberships?.[0];

  const navLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/submit', icon: Upload, label: 'Submit Sample' },
    // Its own entry rather than a tab on the one above: a URL is analysed by
    // driving a browser at it, which is a different act with a different page.
    { to: '/submit-url', icon: Globe, label: 'Submit URL' },
    { to: '/submissions', icon: ListChecks, label: 'Submissions' },
  ];

  return (
    <div className="flex flex-col min-h-full w-full">
      <div className="sidebar-header flex justify-between">
        <Link
          to="/dashboard"
          className="logo min-w-0"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <LogoLockup size={22} hoverSweep className="text-ink-50" />
          <span className="logo-text truncate text-ink-400 font-normal normal-case">Sandbox</span>
        </Link>
        {onClose && (
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div className="nav-section">
        <span className="nav-heading">Platform</span>
        <nav className="nav-menu">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => cn('nav-item', isActive && 'active')}
            >
              <link.icon />
              <span>{link.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="nav-section mt-auto">
        <span className="nav-heading">Administration</span>
        <nav className="nav-menu">
          {orgId !== 'all' && (orgId || managedOrg) && (
            <NavLink
              to={`/org/${orgId ?? managedOrg?.org_id}/settings/api-keys`}
              className={({ isActive }) =>
                cn('nav-item', (isActive || isOrgSettingsArea) && 'active')
              }
            >
              <Building2 />
              <span>Organization</span>
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => cn('nav-item', (isActive || isAdminArea) && 'active')}
            >
              <Settings />
              <span>System</span>
            </NavLink>
          )}
          <NavLink to="/profile" className={({ isActive }) => cn('nav-item', isActive && 'active')}>
            <UserIcon />
            <span>Profile</span>
          </NavLink>
        </nav>
      </div>

      <div className="user-footer">
        <div className="user-avatar">
          {user?.display_name?.charAt(0).toUpperCase() ||
            user?.email?.charAt(0).toUpperCase() ||
            '?'}
        </div>
        <div className="user-info truncate">
          <span className="user-name truncate block">{user?.display_name || user?.email}</span>
          <span className="user-role truncate block">
            {user?.is_admin ? 'Administrator' : 'Operator'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => logout()}
          className="btn-icon ml-auto"
          aria-label="Log out"
        >
          <LogOut size={16} />
        </button>
      </div>
    </div>
  );
}
